import { useEffect, useMemo, useState } from "react";
import client from "@/lib/api";
import { VIDEOS } from "@/constants/testIds";
import Loader from "@/components/Loader";
import ShareButtons from "@/components/ShareButtons";
import usePageTitle from "@/hooks/usePageTitle";

export default function Videos() {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  usePageTitle("Videos", "Public speeches, on-camera interviews, federation announcements and conversations with Shri Nitin Narang.");

  useEffect(() => {
    (async () => {
      const [v, c] = await Promise.all([client.get("/videos"), client.get("/videos/categories")]);
      setItems(v.data.items); setCats(c.data.categories);
      if (v.data.items.length) setActive(v.data.items[0]);
    })();
  }, []);

  const filtered = useMemo(() => (items || []).filter(v => cat === "All" || v.category === cat), [items, cat]);

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      <div className="mb-12">
        <div className="eyebrow mb-4">Videos</div>
        <h1 className="serif-title text-5xl lg:text-6xl text-[#0a1628] mb-4">Speeches, interviews & media.</h1>
        <p className="text-[#4a4a4a] text-[17px] leading-relaxed max-w-3xl">
          A curated reel of public addresses, on-camera interviews and federation announcements.
        </p>
      </div>

      {active && (
        <div className="grid lg:grid-cols-12 gap-10 mb-16 border hairline p-6 lg:p-10 bg-white">
          <div className="lg:col-span-8">
            <div className="aspect-video bg-[#0a1628]">
              <iframe
                key={active.youtube_id}
                src={`https://www.youtube.com/embed/${active.youtube_id}?rel=0&modestbranding=1&playsinline=1`}
                title={active.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="eyebrow text-[#b8975d] mb-3">{active.category}</div>
            <h2 className="font-display text-2xl lg:text-3xl text-[#0a1628] mb-3 leading-tight">{active.title}</h2>
            <div className="text-[12px] tracking-[0.18em] uppercase text-[#6b6b6b] mb-4">
              {active.channel} {active.date && ` · ${new Date(active.date).toLocaleDateString("en-IN", {month: "short", year: "numeric"})}`}
            </div>
            <p className="text-[#4a4a4a] text-[14px] leading-relaxed mb-6">{active.description}</p>
            <ShareButtons title={active.title} url={`https://youtu.be/${active.youtube_id}`} excerpt={active.description} />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-10 pb-6 border-b hairline" data-testid={VIDEOS.filter}>
        {["All", ...cats].map(c => (
          <button key={c}
            data-testid={`video-cat-${c.toLowerCase()}`}
            onClick={() => setCat(c)}
            className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase border transition-all ${
              cat === c ? "bg-[#0a1628] text-[#faf8f3] border-[#0a1628]" : "border-[#d9d4c7] text-[#2c2c2c] hover:border-[#0a1628]"
            }`}>{c}</button>
        ))}
      </div>

      {!items ? <Loader label="Loading videos" /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={VIDEOS.list}>
          {filtered.map(v => (
            <button key={v.id} onClick={() => {
              setActive(v);
              // smooth-scroll the player into view (not the very top), so the
              // video begins playing in the visible area without a jarring jump.
              setTimeout(() => {
                const el = document.querySelector('iframe[title]');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 60);
            }}
              data-testid={VIDEOS.item(v.id)}
              className="text-left group">
              <div className="relative aspect-video overflow-hidden border hairline">
                <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                <div className="absolute inset-0 bg-[rgba(10,22,40,0.2)] group-hover:bg-[rgba(10,22,40,0.4)] transition-colors flex items-center justify-center">
                  <div className="w-12 h-12 border border-[#b8975d] bg-[rgba(10,22,40,0.65)] backdrop-blur flex items-center justify-center">
                    <i className="fa-solid fa-play text-[#b8975d]"></i>
                  </div>
                </div>
              </div>
              <div className="pt-4">
                <div className="eyebrow text-[#b8975d] mb-1">{v.category}</div>
                <h3 className="font-display text-lg text-[#0a1628] leading-snug">{v.title}</h3>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b6b] mt-2">{v.channel}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </main>
  );
}
